// The bell in a channel's (or DM's) header: how loudly this conversation notifies me, plus turning push
// notifications on for this device. A small menu under the bell on desktop, a bottom sheet on phones.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { effectiveNotifyLevel, MUTE_OPTIONS, muteActive, muteLabel, type Channel, type MuteFor, type NotifyLevel, type ServerNotify } from "@bloom/shared";
import { checkPush, clearNotifications, disablePush, enablePush, isIOS, testPush, type PushStatus } from "../push.ts";
import { isNarrow } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";
import "./notify.css";

export const LEVEL_OPTIONS: { level: NotifyLevel; label: string; hint: string }[] = [
  { level: "all", label: "All messages", hint: "Every new message" },
  { level: "mentions", label: "Only @mentions", hint: "When someone @mentions you or @everyone" },
  { level: "none", label: "Nothing", hint: "No notifications, not even @mentions" },
];
const levelLabel = (l: NotifyLevel) => LEVEL_OPTIONS.find((o) => o.level === l)!.label;

export function BellIcon({ level, muted = false, size = 18 }: { level: NotifyLevel; muted?: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" fill={level === "all" && !muted ? "currentColor" : "none"} />
      {(level === "none" || muted) && <path d="M3 3l18 18" />}
      {level === "all" && !muted && (
        <>
          <path d="M2 8c0-2.2.7-4.3 2-6" />
          <path d="M22 8a10 10 0 0 0-2-6" />
        </>
      )}
    </svg>
  );
}

/** Mute for 24 hours / a week / until unmuted, or "Muted until …" with Unmute. Used by the bell and server settings. */
export function MuteControl({ what, until, onMute }: { what: string; until: number | undefined; onMute: (f: MuteFor | null) => void }) {
  const [choosing, setChoosing] = useState(false);
  if (muteActive(until)) {
    return (
      <div className="notify-mute on" role="group" aria-label={`Mute ${what}`}>
        <BellIcon level="none" size={20} />
        <span className="notify-option-text">
          <span className="notify-option-label">{muteLabel(until!)}</span>
          <span className="notify-option-hint">Only @mentions notify you. No unread dot.</span>
        </span>
        <button type="button" className="btn btn-small" onClick={() => onMute(null)}>Unmute</button>
      </div>
    );
  }
  return (
    <div className="notify-mute" role="group" aria-label={`Mute ${what}`}>
      <button type="button" className="notify-option" aria-expanded={choosing} onClick={() => setChoosing((v) => !v)}>
        <BellIcon level="none" size={20} />
        <span className="notify-option-text">
          <span className="notify-option-label">Mute {what}</span>
          <span className="notify-option-hint">Only @mentions get through, and it stops showing unread</span>
        </span>
        <span className="notify-check" aria-hidden="true">{choosing ? "▴" : "▾"}</span>
      </button>
      {choosing && (
        <div className="notify-mute-options">
          {MUTE_OPTIONS.map((o) => (
            <button key={o.for} type="button" className="notify-mute-option" onClick={() => { setChoosing(false); onMute(o.for); }}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export interface NotifyMenuProps {
  channel: Pick<Channel, "id" | "name" | "kind">;
  /** My level, if I picked one (undefined: the server's, or "all" in a DM). */
  level: NotifyLevel | undefined;
  /** My settings for the whole server. */
  server: ServerNotify;
  /** This conversation's own mute. */
  mutedUntil: number | undefined;
  onChange: (level: NotifyLevel | null) => void;
  onMute: (f: MuteFor | null) => void;
  onServerSettings: () => void;
}

/**
 * One conversation's notification menu: mute, then the level, then this device's push. Shown by the bell in the
 * channel header and by a long press on a row of the channel list (phones). `onClose`: a level was picked or the
 * server settings were asked for. A row's menu (ChannelRowMenu.tsx) adds its own items: `before` the mute (Mark as
 * read) and `after` the levels (Copy link, Channel settings…, Close conversation).
 */
export function NotifyMenu({ channel, level: chosen, server, mutedUntil, onChange, onMute, onServerSettings, onClose, heading, before, after }: NotifyMenuProps & { onClose: () => void; heading?: string; before?: ReactNode; after?: ReactNode }) {
  const isDm = channel.kind === "dm";
  const inherited = effectiveNotifyLevel(channel.kind, null, server);
  const level = effectiveNotifyLevel(channel.kind, chosen, server);
  const ownMute = muteActive(mutedUntil);
  const serverMute = !isDm && muteActive(server.mutedUntil);
  const where = isDm ? "this conversation" : `#${channel.name}`;
  const pick = (l: NotifyLevel | null) => {
    onChange(l);
    onClose();
  };
  const option = (l: NotifyLevel | null, label: string, hint: string, on: boolean, shown: NotifyLevel) => (
    <button key={l ?? "default"} type="button" role="menuitemradio" aria-checked={on} className={`notify-option${on ? " on" : ""}`} onClick={() => pick(l)}>
      <BellIcon level={shown} size={20} />
      <span className="notify-option-text">
        <span className="notify-option-label">{label}</span>
        <span className="notify-option-hint">{hint}</span>
      </span>
      <span className="notify-check" aria-hidden="true">{on ? "✓" : ""}</span>
    </button>
  );
  return (
    <div className="notify-menu" role="menu" aria-label={`Notifications for ${heading ?? where}`}>
      {heading && <div className="notify-heading">{heading}</div>}
      {before}
      <MuteControl what={where} until={mutedUntil} onMute={onMute} />
      {serverMute && !ownMute && (
        <div className="notify-inherited" role="note">
          {muteLabel(server.mutedUntil!).replace(/^Muted/, "The whole server is muted")}: only @mentions notify you here.
        </div>
      )}
      <div className="notify-title">Notify me about {where}</div>
      {isDm
        ? LEVEL_OPTIONS.map((o) => option(o.level, o.label, o.hint, level === o.level, o.level))
        : [
            option(null, "Use server default", `${levelLabel(inherited)}, from your server settings`, chosen === undefined, inherited),
            ...LEVEL_OPTIONS.map((o) => option(o.level, o.label, o.hint, chosen === o.level, o.level)),
          ]}
      {!isDm && (
        <button type="button" className="link-btn notify-server-link" onClick={() => { onClose(); onServerSettings(); }}>
          Server notification settings…
        </button>
      )}
      {after}
      <DevicePush />
    </div>
  );
}

/** The channel header's bell: the notification menu in a popover (desktop) or a sheet (phones). Phones reach it from the header's ⋯ menu (#348), so the bell itself is desktop-only. */
export function NotifyBell(props: NotifyMenuProps & { serverId: string }) {
  const { serverId, channel, server, mutedUntil } = props;
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const level = effectiveNotifyLevel(channel.kind, props.level, server);
  const muted = muteActive(mutedUntil) || (channel.kind !== "dm" && muteActive(server.mutedUntil));

  // Opening a channel clears its notifications from this device.
  useEffect(() => clearNotifications(`bloom:${serverId}:${channel.id}`), [serverId, channel.id]);

  // Desktop menu: closes on a click outside or Escape.
  useEffect(() => {
    if (!open || isNarrow()) return;
    const onDown = (e: PointerEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const body = <NotifyMenu {...props} onClose={() => setOpen(false)} />;
  return (
    <span className="notify-wrap desktop-only" ref={wrap}>
      <button
        type="button"
        className={`icon-btn head-btn notify-bell${level === "none" || muted ? " muted" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Notifications: ${levelLabel(level)}${muted ? ", muted" : ""}`}
        title="Notification settings"
      >
        <BellIcon level={level} muted={muted} size={19} />
      </button>
      {open &&
        (isNarrow() ? (
          <Sheet label="Notification settings" onClose={() => setOpen(false)}>
            {body}
          </Sheet>
        ) : (
          <div className="notify-popover">{body}</div>
        ))}
    </span>
  );
}

/** Whether this device gets push notifications at all, with the one action that makes sense from here. */
export function DevicePush() {
  const [status, setStatus] = useState<PushStatus | "loading">("loading");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    void checkPush().then(setStatus, () => setStatus("unsupported"));
  }, []);
  if (status === "loading" || status === "unconfigured") return null;

  const run = async (fn: () => Promise<PushStatus>) => {
    setBusy(true);
    setNote(null);
    try {
      setStatus(await fn());
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="notify-device">
      {status === "on" && (
        <>
          <div className="notify-device-row">
            <span className="notify-dot on" aria-hidden="true" />
            <span className="notify-device-text">Push notifications are on for this device.</span>
          </div>
          <div className="notify-device-actions">
            <button
              type="button"
              className="link-btn"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const r = await testPush().catch((e: Error) => e);
                setBusy(false);
                setNote(r instanceof Error ? r.message : `Test sent to ${r.sent} of ${r.devices} device${r.devices === 1 ? "" : "s"}.`);
              }}
            >
              Send a test
            </button>
            <button type="button" className="link-btn quiet" disabled={busy} onClick={() => run(disablePush)}>
              Turn off on this device
            </button>
          </div>
        </>
      )}
      {status === "off" && (
        <>
          <div className="notify-device-text">Get notified on this device when you're not looking at Bloom.</div>
          <button type="button" className="btn btn-primary notify-enable" disabled={busy} onClick={() => run(enablePush)}>
            {busy ? "Turning on…" : "Turn on notifications"}
          </button>
        </>
      )}
      {status === "denied" && (
        <div className="notify-device-text">
          Notifications are blocked for Bloom. Allow them in your browser's site settings{isIOS() ? " (Settings › Notifications › Bloom)" : ""}, then come back here.
        </div>
      )}
      {status === "ios-needs-install" && (
        <div className="notify-ios">
          <strong>On iPhone, add Bloom to your Home Screen first.</strong>
          <ol>
            <li>
              Tap <ShareIcon /> <b>Share</b> in Safari.
            </li>
            <li>
              Choose <b>Add to Home Screen</b>.
            </li>
            <li>Open Bloom from the new icon and turn notifications on here.</li>
          </ol>
          <span className="muted">Needs iOS 16.4 or later.</span>
        </div>
      )}
      {status === "unsupported" && <div className="notify-device-text muted">This browser can't show notifications.</div>}
      {note && <div className="notify-note" role="status">{note}</div>}
    </div>
  );
}

const ShareIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: "-2px" }}>
    <path d="M12 3v12M8 7l4-4 4 4" />
    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
  </svg>
);
